import { InfoCircleOutlined } from '@ant-design/icons';
import { Checkbox, Divider, Form, Input, InputNumber, Tooltip } from 'antd';
import { useEffect, useState } from 'react';
import { NUMBER_FIELD_TYPES } from '../../../../../common/constants';
import { formValidatorRules } from '../../../../../common/utils';
import ModalComponent from '../../../../../components/ModalComponent';
import NumberComponent from '../../../../../components/NumberComponent';

const { Item } = Form;
const { positiveNumber } = formValidatorRules;
const { TextArea } = Input;

const EditInventoryModal = ({
  isModalOpen,
  setIsModalOpen,
  onFinish,
  loadings,
  form,
  inventory,
  autoReturnMinDays = 14,
}) => {
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);
  const [currentlyAvailableStock, setCurrentlyAvailableStock] = useState(0);
  const handleCancel = () => {
    setIsModalOpen(false);
    form?.resetFields();
  };

  useEffect(() => {
    form.setFieldsValue({
      totalStock:
        parseInt(currentlyAvailableStock, 10) +
          parseInt(inventory?.rentedOut, 10) +
          parseInt(inventory?.damagedStock, 10) || 0,
    });
  }, [currentlyAvailableStock, isModalOpen]);

  useEffect(() => {
    const initialValues = {
      ...inventory,
      totalStock: parseInt(inventory?.totalStock, 10),
      oldQuantity: inventory?.availableStock,
      damagedStock: inventory?.damagedStock,
      allowPreOrder: inventory?.allowPreOrder,
      preOrderLimit: inventory?.preOrderLimit,
      forceAvailable: inventory?.forceAvailable,
      isNewUserOnly: inventory?.isNewUserOnly ?? false,
      autoReturn: inventory?.autoReturn ?? false,
      autoReturnInDays: inventory?.autoReturn
        ? inventory?.autoReturnInDays ?? autoReturnMinDays
        : autoReturnMinDays,
      rank: inventory?.rank,
    };
    form.setFieldsValue(initialValues);
  }, [inventory, isModalOpen, autoReturnMinDays]);

  return (
    <>
      <ModalComponent
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={form?.submit}
        title={`Update Total Stock of ${inventory?.toy?.name}`}
        okText="Save"
        confirmLoading={loadings}
        destroyOnClose
        forceRender
        bodyStyle={{ maxHeight: '65vh', overflowY: 'auto' }}
        wrapClassName="edit-inventory-modal"
      >
        <Divider />
        <Form
          form={form}
          onFinish={onFinish}
          autoComplete="off"
          layout="vertical"
          validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
          onFinishFailed={() => setIsValidateTrigger(true)}
        >
          <Item
            label="Available for booking As Per System"
            name="oldQuantity"
            rules={[
              {
                required: true,
                message: 'Please Enter Available for booking As Per System!',
              },
              positiveNumber,
            ]}
          >
            <NumberComponent
              allowClear
              placeholder="Enter Available for booking As Per System"
              isFractional={false}
              disabled
              type={NUMBER_FIELD_TYPES?.STOCK}
            />
          </Item>
          <Item
            label="Physically Available"
            name="currentlyAvailable"
            rules={[positiveNumber]}
          >
            <NumberComponent
              allowClear
              placeholder="Enter Physically Available"
              isFractional={false}
              onChange={(e) =>
                setCurrentlyAvailableStock(e?.target?.value || 0)
              }
              type={NUMBER_FIELD_TYPES?.STOCK}
            />
          </Item>
          <Item label="Rented Out" name="rentedOut" required>
            <Input allowClear placeholder="Enter Rented Out" disabled />
          </Item>
          <Item label="Damaged Stock" name="damagedStock" required>
            <Input allowClear disabled placeholder="Enter Damaged Stock" />
          </Item>
          <Item label="Total Stock" name="totalStock" required>
            <Input allowClear placeholder="Enter Total Stock" disabled />
          </Item>
          <Item name="isNewUserOnly" valuePropName="checked" className="mb-12">
            <Checkbox>
              New User Only
              <Tooltip title="If checked - Only new users see this as available. Others see dynamic availability.">
                <InfoCircleOutlined
                  style={{ marginLeft: '6px', color: '#9264A8', fontSize: '12px', verticalAlign: 'middle' }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
            </Checkbox>
          </Item>
          <Item name="allowPreOrder" valuePropName="checked" className="mb-12">
            <Checkbox
              onChange={(e) => {
                if (!e.target.checked) {
                  form.setFieldsValue({ forceAvailable: false });
                }
              }}
            >
              Allow Pre Order
            </Checkbox>
          </Item>
          <Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.allowPreOrder !== curr.allowPreOrder
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('allowPreOrder') ? (
                <Item name="forceAvailable" valuePropName="checked" className="mb-12">
                  <Checkbox>Allow Force Order</Checkbox>
                </Item>
              ) : null
            }
          </Item>
          <Item
            noStyle
            shouldUpdate={(prev, curr) =>
              prev.allowPreOrder !== curr.allowPreOrder
            }
          >
            {({ getFieldValue }) =>
              getFieldValue('allowPreOrder') ? (
                <Item
                  label="Max Pre Order Allowed"
                  name="preOrderLimit"
                  rules={[
                    {
                      validator(_, value) {
                        if (
                          value === undefined ||
                          value === null ||
                          value === '' ||
                          parseInt(value, 10) < 1
                        ) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject(
                            'Max pre order allowed should be greater than 0',
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Enter Max Pre Order Allowed"
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                    onStep={(value) => {
                      if (value < 1) {
                        form.setFieldsValue({ preOrderLimit: 1 });
                      }
                    }}
                  />
                </Item>
              ) : null
            }
          </Item>
          <Item name="autoReturn" valuePropName="checked" className="mb-12">
            <Checkbox
              onChange={(e) => {
                if (!e.target.checked) {
                  form.setFieldsValue({ autoReturnInDays: autoReturnMinDays });
                }
              }}
            >
              Allow Auto Return
            </Checkbox>
          </Item>
          <Item
            noStyle
            shouldUpdate={(prev, curr) => prev.autoReturn !== curr.autoReturn}
          >
            {({ getFieldValue }) =>
              getFieldValue('autoReturn') ? (
                <Item
                  label="Auto Return In Days"
                  name="autoReturnInDays"
                  rules={[
                    {
                      required: true,
                      message: 'Please enter auto return days!',
                    },
                    {
                      validator(_, value) {
                        if (!value && value !== 0) return Promise.resolve();
                        if (parseInt(value, 10) < autoReturnMinDays) {
                          // eslint-disable-next-line prefer-promise-reject-errors
                          return Promise.reject(
                            `Auto return days must be at least ${autoReturnMinDays}!`,
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <NumberComponent
                    allowClear
                    placeholder="Enter Auto Return In Days"
                    isFractional={false}
                    min={autoReturnMinDays}
                    type={NUMBER_FIELD_TYPES?.STOCK}
                  />
                </Item>
              ) : null
            }
          </Item>
          <Item
            label="Rank"
            name="rank"
            rules={[
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === '') {
                    return Promise.resolve();
                  }
                  const num = Number(value);
                  const strVal = String(value).trim();
                  if (
                    Number.isNaN(num) ||
                    !Number.isInteger(num) ||
                    strVal.includes('.') ||
                    strVal.includes('e') ||
                    strVal.includes('E') ||
                    num < 1 ||
                    num > 6
                  ) {
                    // eslint-disable-next-line prefer-promise-reject-errors
                    return Promise.reject('Rank must be between 1 to 6');
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <InputNumber
              placeholder="Select Rank"
              step={1}
              maxLength={1}
              style={{ width: '100%' }}
              onKeyDown={(e) => {
                const allowed = [
                  'Backspace',
                  'Delete',
                  'Tab',
                  'ArrowLeft',
                  'ArrowRight',
                  'ArrowUp',
                  'ArrowDown',
                  'Home',
                  'End',
                  'Enter',
                ];
                const isDigit = e.key >= '0' && e.key <= '9';
                const isDot = e.key === '.';

                if (isDigit || isDot) {
                  const { target } = e;
                  const val = target.value || '';
                  const hasSelection =
                    target.selectionStart !== target.selectionEnd;
                  if (val.length >= 1 && !hasSelection) {
                    e.preventDefault();
                    return;
                  }
                }

                if (!isDigit && !isDot && !allowed.includes(e.key)) {
                  e.preventDefault();
                }
              }}
              onStep={(value) => {
                if (value > 6) {
                  form.setFieldsValue({ rank: 6 });
                } else if (value < 1) {
                  form.setFieldsValue({ rank: 1 });
                }
              }}
            />
          </Item>
          <Item name="reason" label="Reason">
            <TextArea
              rows={4}
              allowClear
              placeholder="Enter Reason"
              showCount
              maxLength={700}
            />
          </Item>
        </Form>
      </ModalComponent>
    </>
  );
};

export default EditInventoryModal;
